// A form's own checks, in the UI's words (UX-010, the 2026-11-01 review).
//
// Every form here is `SpecForm`, and it used to leave the checking to the
// browser: a native bubble, one field at a time, in the browser's language
// (*"Veuillez renseigner ce champ."* in an English screen), gone on blur and
// told to nobody once it had gone. The form now sets `noValidate` and asks
// this file instead: on submit every field, then, as a person types, only the
// fields already in error. A message names the field by its label, never by
// the API's name for it, and is drawn under the field with `aria-invalid` and
// `aria-describedby` pointing at it.
//
// What is checked is exactly what the browser checked before — `required`,
// `type="email"`, `pattern`, `minLength`, `maxLength`, and the same per column
// of a `rows` field — so a value the form accepts is one it accepted before.
import { useRef, type ChangeEvent, type ReactNode } from "react";
import type { FieldSpec, FormValues, Row, RowColumn } from "./ui";

const empty = (v: unknown) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** The browser's `pattern`: the whole value, unicode-aware. A pattern this
 * engine cannot compile checks nothing, as the browser ignores one too. */
function matches(pattern: string, value: string): boolean {
  try {
    return new RegExp(`^(?:${pattern})$`, "u").test(value);
  } catch {
    return true;
  }
}

/** A simple address check, the shape the browser's `type="email"` asks for:
 * something, an at sign, something with no space. The server is the judge. */
const EMAIL = /^[^\s@]+@[^\s@]+$/;

function textError(label: string, value: string, c: { required?: boolean; pattern?: string; minLength?: number; maxLength?: number; type?: string; placeholder?: string }): string | undefined {
  if (value === "") return c.required ? `${label} is required.` : undefined;
  if (c.type === "email" && !EMAIL.test(value)) return "Enter an email address, like name@example.com.";
  if (c.minLength !== undefined && value.length < c.minLength) return `${label} needs at least ${c.minLength} characters.`;
  if (c.maxLength !== undefined && value.length > c.maxLength) return `${label} takes at most ${c.maxLength} characters.`;
  if (c.pattern && !matches(c.pattern, value)) return `${label} is not in the expected form.${c.placeholder ? ` For example: ${c.placeholder}.` : ""}`;
  return undefined;
}

function columnError(c: RowColumn, row: Row, i: number): string | undefined {
  const label = `${c.label} ${i + 1}`;
  const v = row[c.name] ?? "";
  if (c.type === "number" && v !== "") {
    const n = Number(v);
    if (Number.isNaN(n)) return `${label} must be a number.`;
    if (c.min !== undefined && n < c.min) return `${label} must be at least ${c.min}.`;
    if (c.max !== undefined && n > c.max) return `${label} must be at most ${c.max}.`;
    return undefined;
  }
  return textError(label, v, c);
}

/** What is wrong with one field's value, in words, or nothing. A disabled
 * field and a checkbox are never wrong: nobody can answer them otherwise. */
export function fieldError(f: FieldSpec, v: FormValues[string]): string | undefined {
  if (f.disabled || f.type === "checkbox") return undefined;
  if (f.type === "rows") {
    const list = Array.isArray(v) ? v : [];
    if (f.required && list.length === 0) return `${f.label} needs at least one entry.`;
    for (let i = 0; i < list.length; i++) {
      for (const c of f.columns ?? []) {
        const e = columnError(c, list[i]!, i);
        if (e) return e;
      }
    }
    return undefined;
  }
  if (f.control) return f.required && empty(v) ? `${f.label} is required.` : undefined;
  if (f.type === "select") return f.required && empty(v) ? `Choose a value for ${f.label}.` : undefined;
  if (f.type === "number") {
    if (empty(v)) return f.required ? `${f.label} is required.` : undefined;
    return Number.isNaN(Number(v)) ? `${f.label} must be a number.` : undefined;
  }
  return textError(f.label, empty(v) ? "" : String(v), { ...f, type: f.type });
}

/** Every field's error, by field name; a field with none is absent. */
export function formErrors(fields: FieldSpec[], values: FormValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const e = fieldError(f, values[f.name]);
    if (e) out[f.name] = e;
  }
  return out;
}

/** Whether a field is optional enough to say so beside its label: an answer
 * the person may leave out. A required field is told by the control itself
 * (`aria-required`), a checkbox and a disabled field have nothing to leave out. */
export const optional = (f: FieldSpec) => !f.required && !f.disabled && f.type !== "checkbox";

/** The line above a form's buttons after a submit that did not go: how many
 * fields to look at, said once and politely loud (`role="alert"`). */
export function ErrorCount({ count }: { count: number }) {
  if (count === 0) return null;
  return <p className="form-error-count" role="alert">{count === 1 ? "1 field needs your attention." : `${count} fields need your attention.`}</p>;
}

/** Whether two forms' values say the same thing: a blank and an absent value
 * are the same answer, so opening and closing a select is no edit. */
export function sameValues(a: FormValues, b: FormValues): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const x = a[k], y = b[k];
    if (empty(x) && empty(y)) continue;
    if (JSON.stringify(x) !== JSON.stringify(y)) return false;
  }
  return true;
}

/** A file input drawn as a labelled button and a line naming what was chosen
 * (UX-010). The native control writes its own words in the browser's language
 * (*"Sélect. fichiers / Aucun fichier choisi"*). The input stays — the field's
 * `<label>` still names it and a test still reaches it by that label — but is
 * out of the tab order; the button is the control a person uses. */
export function FilePicker({ id, accept, multiple, button, chosen, onChange }: {
  id: string; accept?: string; multiple?: boolean;
  /** The button's words: the act, naming what to pick. */
  button: string;
  /** What is chosen, in words, or nothing; "No file chosen" stands for nothing. */
  chosen?: ReactNode;
  onChange(e: ChangeEvent<HTMLInputElement>): void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="file-picker">
      <input ref={input} id={id} type="file" className="sr-only" tabIndex={-1} accept={accept} multiple={multiple} onChange={onChange} />
      <button type="button" className="btn" aria-describedby={`${id}-chosen`} onClick={() => input.current?.click()}>{button}</button>
      <span id={`${id}-chosen`} className="small muted">{chosen ?? "No file chosen"}</span>
    </div>
  );
}
